/**
 * Module Initialization
 *
 * Registers base screens with the ModuleRegistry, then layers on every screen
 * override declared by a tenant bundle under `customize/`. Imported once at
 * startup from `main.tsx`.
 *
 * Adding a tenant override is a `componentOverrides` entry in that tenant's
 * bundle -- this file never names a tenant.
 */

import React from 'react';
import { getTenantCustomizations } from '@customize/registry';
import { moduleRegistry } from './ModuleRegistry';

import LoginPage from '@pages/main/auth/LoginPage';
import HomeVisitPatientPage from '@pages/main/homeVisitPatient/HomeVisitPatientPage';
import VisitSchedulePage from '@pages/main/visitSchedule/VisitSchedulePage';
import DocumentTemplatePage from '@pages/main/documentTemplate/DocumentTemplatePage';
import { MassagerPage } from '@pages/main/massager';
import { PatientDmInfoEditPage } from '@pages/main/patient';

/** Base implementations, keyed by the path the router asks for. */
function registerBaseComponents(): void {
  moduleRegistry.registerComponent('patient/PatientDmInfoEdit', () =>
    Promise.resolve({ default: PatientDmInfoEditPage as React.ComponentType<unknown> })
  );
  moduleRegistry.registerComponent('massager/MassagerList', () =>
    Promise.resolve({ default: MassagerPage as React.ComponentType<unknown> })
  );
  moduleRegistry.registerComponent('documentTemplate/DocumentTemplate', () =>
    Promise.resolve({ default: DocumentTemplatePage as React.ComponentType<unknown> })
  );
  // 訪問予定表(患者毎) at `visits/patient_init`. A seam rather than a direct
  // element on the route, because KEiLOG draws a different screen there
  // (SCR-064 利用者予定) while every other site keeps this one.
  moduleRegistry.registerComponent('visit/HomeVisitPatientList', () =>
    Promise.resolve({ default: HomeVisitPatientPage as React.ComponentType<unknown> })
  );
  // 訪問予定 (week calendar) at `visits/schedule` and its two longer shapes. A seam for the same
  // reason: a tenant may draw another screen there while every other site keeps this one.
  moduleRegistry.registerComponent('visit/VisitSchedule', () =>
    Promise.resolve({ default: VisitSchedulePage as React.ComponentType<unknown> })
  );
  // Public, pre-auth: the loader that renders it passes the site type from the
  // URL, since no clinic is signed in to read one from.
  moduleRegistry.registerComponent('auth/Login', () =>
    Promise.resolve({ default: LoginPage as React.ComponentType<unknown> })
  );
}

/** Per-tenant replacements declared in `customize/<tenant>`. */
function registerTenantOverrides(): void {
  for (const tenant of getTenantCustomizations()) {
    for (const [path, loader] of Object.entries(tenant.componentOverrides ?? {})) {
      moduleRegistry.registerTenantOverride(path, tenant.code, loader);
    }
  }
}

registerBaseComponents();
registerTenantOverrides();
